import React, { useState } from 'react';
import { Modal, Text, Button, GradientBar, Icon } from '@heartinternet/hi-ui';
import type { UpgradeGrid, UpgradeTier } from '../types';
import './Pro2SharedUpgradeModal.css';

const BASKET_URL = '/manage/basket.cgi';

// Tiers low → high; only tiers strictly above the current one are offered.
const TIER_ORDER: UpgradeTier[] = ['lite', 'plus', 'advanced', 'elite'];

interface TierSpec {
  tier: UpgradeTier;
  name: string;
  /** Feature rows, in display order (from manage-hosting.tmpl). */
  features: { label: string; value: string }[];
}

// Hardcoded tier specs mirrored from the legacy template
// (templates/gb/manage-hosting.tmpl, #upgrade-modal). Elite also lists Free SSL.
const TIER_SPECS: Record<UpgradeTier, TierSpec> = {
  lite: {
    tier: 'lite',
    name: 'Lite',
    features: [
      { label: 'Multi-sites', value: '1' },
      { label: 'Subdomains', value: '25' },
      { label: 'Storage', value: '25GB' },
      { label: 'Mailboxes', value: '10' },
      { label: 'Mailbox Size', value: '5GB' },
      { label: 'MySQL Databases', value: '1' },
    ],
  },
  plus: {
    tier: 'plus',
    name: 'Plus',
    features: [
      { label: 'Multi-sites', value: '3' },
      { label: 'Subdomains', value: '50' },
      { label: 'Storage', value: '100GB' },
      { label: 'Mailboxes', value: '50' },
      { label: 'Mailbox Size', value: '5GB' },
      { label: 'MySQL Databases', value: '50' },
    ],
  },
  advanced: {
    tier: 'advanced',
    name: 'Advanced',
    features: [
      { label: 'Multi-sites', value: '5' },
      { label: 'Subdomains', value: 'Unlimited' },
      { label: 'Storage', value: '250GB' },
      { label: 'Mailboxes', value: '100' },
      { label: 'Mailbox Size', value: '10GB' },
      { label: 'MySQL Databases', value: '100' },
    ],
  },
  elite: {
    tier: 'elite',
    name: 'Elite',
    features: [
      { label: 'Multi-sites', value: '10' },
      { label: 'Subdomains', value: 'Unlimited' },
      { label: 'Storage', value: '500GB' },
      { label: 'Mailboxes', value: '200' },
      { label: 'Mailbox Size', value: '10GB' },
      { label: 'MySQL Databases', value: '250' },
      { label: 'Free SSL', value: 'Yes*' },
    ],
  },
};

interface Pro2SharedUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Selected package OID (→ upgrade_for). */
  packageOid: string;
  /** Selected package payment interval; drives price + basket code. */
  paymentInterval: 'monthly' | 'yearly';
  /**
   * Selected package's current tier name (e.g. 'Lite','Plus','Advanced','Elite',
   * or '' / undefined when unknown → show all four). Compare case-insensitively.
   */
  currentTier?: string;
  /** Price grid: { lite|plus|advanced|elite: { monthly, yearly } } (ex-VAT strings). */
  upgradeGrid?: UpgradeGrid;
}

// Tiers strictly above `currentTier` (order lite < plus < advanced < elite).
// Unknown / empty / below-lite currentTier → all four.
const tiersAbove = (currentTier?: string): UpgradeTier[] => {
  const normalised = (currentTier ?? '').trim().toLowerCase();
  const currentIndex = TIER_ORDER.indexOf(normalised as UpgradeTier);
  // -1 (unknown/empty/below lite) → show all four.
  return TIER_ORDER.slice(currentIndex + 1);
};

export const Pro2SharedUpgradeModal: React.FC<Pro2SharedUpgradeModalProps> = ({
  isOpen,
  onClose,
  packageOid,
  paymentInterval,
  currentTier,
  upgradeGrid,
}) => {
  const [submittingTier, setSubmittingTier] = useState<UpgradeTier | null>(null);

  // Only tiers above current AND for which we have a price in the grid.
  const tiers = tiersAbove(currentTier).filter((tier) => Boolean(upgradeGrid?.[tier]));

  const intervalSuffix = paymentInterval === 'monthly' ? ' per month' : ' per year';

  const handleUpgradeClick = (tier: UpgradeTier) => {
    setSubmittingTier(tier);
    // Full-page navigation to the basket (mirrors the legacy web_hosting_upgrade.js).
    // eslint-disable-next-line
    window.location.href = `${BASKET_URL}?type=MULTI&submit-type=PURCHASE&basket-add-code=shared.y1.linux.upgrade.${tier}-${paymentInterval}-card&upgrade_for=${packageOid}`;
  };

  const handleClose = () => {
    setSubmittingTier(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="medium" title="">
      <div
        className={`hi-ui-pro2shared-upgrade-modal hi-ui-pro2shared-upgrade-modal--tiers-${Math.min(
          tiers.length,
          3
        )}`}
      >
        <div className="hi-ui-pro2shared-upgrade-modal__header">
          <Text variant="heading-3" className="hi-ui-pro2shared-upgrade-modal__title">
            Upgrade web hosting
          </Text>
          <Text variant="body-small" className="hi-ui-pro2shared-upgrade-modal__subtitle">
            Get more sites, storage, and mailboxes.
          </Text>
        </div>

        <div className="hi-ui-pro2shared-upgrade-modal__body">
          {tiers.length > 0 && (
            <div className="hi-ui-pro2shared-upgrade-modal__tiers">
              {tiers.map((tier) => {
                const spec = TIER_SPECS[tier];
                // Non-null: `tiers` is filtered to entries present in the grid.
                const price = upgradeGrid![tier]![paymentInterval];
                return (
                  <div key={tier} className="hi-ui-pro2shared-upgrade-modal__card">
                    <Text variant="heading-4" className="hi-ui-pro2shared-upgrade-modal__card-name">
                      {spec.name}
                    </Text>
                    <GradientBar className="hi-ui-pro2shared-upgrade-modal__card-bar" />

                    <div className="hi-ui-pro2shared-upgrade-modal__features">
                      {spec.features.map((feature) => (
                        <div
                          key={feature.label}
                          className="hi-ui-pro2shared-upgrade-modal__feature"
                        >
                          <Icon
                            name="check-circle-grey"
                            size={20}
                            className="hi-ui-pro2shared-upgrade-modal__feature-tick"
                          />
                          <div>
                            <Text
                              variant="body-small"
                              bold
                              inline
                              className="hi-ui-pro2shared-upgrade-modal__feature-label"
                            >
                              {feature.label}
                            </Text>
                            <div className="hi-ui-pro2shared-upgrade-modal__feature-value">
                              {feature.value}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="hi-ui-pro2shared-upgrade-modal__pricing">
                      <Text variant="heading-3" className="hi-ui-pro2shared-upgrade-modal__price">
                        £{price}
                        <Text
                          variant="body-small"
                          inline
                          className="hi-ui-pro2shared-upgrade-modal__interval"
                        >
                          {intervalSuffix}
                        </Text>
                      </Text>
                    </div>

                    <Button
                      variant="primary"
                      size="large"
                      onClick={() => handleUpgradeClick(tier)}
                      loading={submittingTier === tier}
                      disabled={Boolean(submittingTier) && submittingTier !== tier}
                      className="hi-ui-pro2shared-upgrade-modal__cta"
                    >
                      Add to Basket
                    </Button>
                  </div>
                );
              })}
            </div>
          )}

          <div className="hi-ui-pro2shared-upgrade-modal__footer">
            <Text variant="body-small">
              * Only available on yearly packages. If you already assigned an SSL certificate to the
              web hosting package, it will be automatically converted to a free SSL for the duration
              of the package.
            </Text>
            <Text variant="body-small">
              Initial price of upgrade is calculated pro-rata based on the remaining time of your
              current package, then will be at full price on future renewals. All prices shown are
              excluding VAT.
            </Text>
          </div>
        </div>
      </div>
    </Modal>
  );
};
